import { useMemo } from "react";
import { SIGNING_STATUS_FULLY_SIGNED } from "@/features/hr/contracts/consts";
import type { GroupedContract } from "./useGroupedContracts";

export type ApplicationItem =
  | {
      type: "job_application";
      contractId: number;
      candidateId: number | null;
      date: string;
    }
  | {
      type: "order";
      contractId: number;
      candidateId: number | null;
      date: string;
    }
  | {
      type: "resignation_letter";
      resignationId: number;
      date: string;
    }
  | {
      type: "amendment";
      amendmentId: number;
      date: string;
    }
  | {
      type: "legacy_job_application";
      contractId: number;
      url: string;
    }
  | {
      type: "legacy_order";
      contractId: number;
      url: string;
    };

interface UseApplicationItemsParams {
  contracts: GroupedContract[];
}

export function useApplicationItems({ contracts }: UseApplicationItemsParams) {
  const applicationItemsMap = useMemo(() => {
    const map = new Map<number, ApplicationItem[]>();

    contracts.forEach(({ contract, resignationLetters, amendments }) => {
      const items: ApplicationItem[] = [];

      const isContractFullySigned = contract.signing_status === SIGNING_STATUS_FULLY_SIGNED;

      if (isContractFullySigned && contract.job_application) {
        items.push({
          type: "job_application",
          contractId: contract.id,
          candidateId: contract.candidate_application_id,
          date: contract.job_application.created_at || contract.created_at || "",
        });
      }

      if (isContractFullySigned && contract.order_on_hiring) {
        items.push({
          type: "order",
          contractId: contract.id,
          candidateId: contract.candidate_application_id,
          date: contract.order_on_hiring.created_at || contract.created_at || "",
        });
      }

      if (contract.is_legacy_import && contract.legacy_job_application_pdf_url) {
        items.push({
          type: "legacy_job_application",
          contractId: contract.id,
          url: contract.legacy_job_application_pdf_url,
        });
      }

      if (contract.is_legacy_import && contract.legacy_order_pdf_url) {
        items.push({
          type: "legacy_order",
          contractId: contract.id,
          url: contract.legacy_order_pdf_url,
        });
      }

      resignationLetters.forEach((resignation) => {
        items.push({
          type: "resignation_letter",
          resignationId: resignation.id,
          date: resignation.updated_at || resignation.created_at || "",
        });
      });

      amendments.forEach((amendment) => {
        items.push({
          type: "amendment",
          amendmentId: amendment.id,
          date: amendment.updated_at || amendment.created_at || "",
        });
      });

      map.set(contract.id, items);
    });

    return map;
  }, [contracts]);

  return applicationItemsMap;
}

